import { useEffect, useState } from 'react';

/**
 * Subscribes to a CSS media query. Returns true when it matches.
 *
 * Width-only check (no `pointer: fine`) - we want tablet landscape (touch iPad)
 * to count as "wide" too, so split-view layouts kick in for them.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** True at ≥ 900px - the breakpoint where two-pane / split-view layouts engage. */
export function useIsWideViewport(): boolean {
  return useMediaQuery('(min-width: 900px)');
}

/**
 * True at ≥ 1100px - the breakpoint where a workspace may DOCK a detail
 * inspector beside its master list.
 *
 * Higher than `useIsWideViewport` on purpose: on the desktop layout the app
 * nav sidebar already claims ~250px of real width, so on iPad-mini-class
 * landscape widths (1024px) a docked inspector would squeeze the master list
 * and leave the detail pane unusable. Below this breakpoint the workspace
 * pushes the detail as a full page instead (the desktop sidebar chrome still
 * shows from 768px up). Reservations and Orders use this for their
 * master/detail split.
 */
export function useSupportsDockedDetail(): boolean {
  return useMediaQuery('(min-width: 1100px)');
}

/**
 * Back-link text for a settings *root* detail page (one reached from the
 * Settings list). In the desktop master-detail (≥768px) the detail page is the
 * pane root with nothing to return to, so an explicit back button renders as a
 * dead, empty chevron - return `undefined` to drop it (the master list + Done
 * are the way out). On mobile the page is pushed onto the Settings stack, so a
 * real "Back" is needed.
 */
export function useSettingsDetailBackLink(): string | undefined {
  return useMediaQuery('(min-width: 768px)') ? undefined : 'Back';
}
